import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { Button, Icon, Notice, TextField } from "@conuti-das/prince-ui";
import { Flex, Spinner, Stack, princeIcon } from "@/ui-prince";
import { marked } from "marked";
import { changelogApi } from "@/lib/changelogApi";
import { useRunnerHealth } from "@/hooks/useRunnerHealth";
import type { ChangelogEntry } from "@/types/changelog";
import "./changelogDialog.css";

type Props = {
  entry: ChangelogEntry;
  runId: string;
  entryKey: string;
  /** Schon angelegtes Ticket aus dem lokalen Review-State (verhindert Doppel-Anlage). */
  jiraKey?: string;
  onJiraCreated: (jiraKey: string, jiraUrl: string) => void;
  /** Meldet ungespeicherte Bearbeitung (Titel/Text weicht vom KI-Entwurf ab). */
  onDirtyChange?: (dirty: boolean) => void;
};

type Phase = "idle" | "loading" | "ready" | "error";

function message(exc: unknown): string {
  return String((exc as Error)?.message ?? exc);
}

/** KI-vorformuliertes Ticket (Als … möchte ich … damit …) — inline editierbar, direkt nach JIRA. */
export function TicketDraftSection({ entry, runId, entryKey, jiraKey, onJiraCreated, onDirtyChange }: Props) {
  const { online } = useRunnerHealth();
  const ids = useId();
  // Liegt schon ein Ticket vor, wird nicht automatisch ein neuer (kostenpflichtiger) Entwurf erzeugt.
  const [phase, setPhase] = useState<Phase>(jiraKey ? "idle" : "loading");
  const [error, setError] = useState("");
  const [warnings, setWarnings] = useState<string[]>([]);
  const [baseline, setBaseline] = useState({ title: "", body: "" });
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [jira, setJira] = useState<{ key: string; url: string } | null>(null);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);

  const generate = useCallback(() => {
    setPhase("loading");
    setError("");
    setCreateError("");
    setConfirmRegenerate(false);
    changelogApi
      .draftTicket(entry)
      .then((d) => {
        setTitle(d.title);
        setBody(d.body_markdown);
        setBaseline({ title: d.title, body: d.body_markdown });
        setWarnings(d.warnings ?? []);
        setPhase("ready");
      })
      .catch((exc) => {
        setError(message(exc));
        setPhase("error");
      });
  }, [entry]);

  // Einmal beim Öffnen der Section — neu generieren nur per Button.
  useEffect(() => {
    if (!jiraKey) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dirty = phase === "ready" && !jira && (title !== baseline.title || body !== baseline.body);
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const previewHtml = useMemo(() => marked.parse(body || "") as string, [body]);

  const regenerate = () => {
    if (dirty) setConfirmRegenerate(true);
    else generate();
  };

  const createJira = () => {
    setCreating(true);
    setCreateError("");
    changelogApi
      .createJira(runId, entryKey, { title: title.trim(), body_markdown: body })
      .then((res) => {
        setJira({ key: res.jira_key, url: res.jira_url });
        onJiraCreated(res.jira_key, res.jira_url);
      })
      .catch((exc) => setCreateError(message(exc)))
      .finally(() => setCreating(false));
  };

  if (phase === "idle") {
    return (
      <Stack className="cl-v2-stack">
        <Notice tone="positive">
          Für diese Änderung wurde bereits in JIRA angelegt: <strong>{jiraKey}</strong>
        </Notice>
        <Flex justifyContent="Start">
          <Button variant="plain" onPress={generate}>Trotzdem neuen Entwurf erzeugen</Button>
        </Flex>
      </Stack>
    );
  }

  if (phase === "loading") {
    return (
      <Flex alignItems="Center" className="cl-v2-busy">
        <Spinner />
        <span className="muted">KI formuliert das Ticket vor …</span>
      </Flex>
    );
  }

  if (phase === "error") {
    return (
      <Stack className="cl-v2-stack">
        <Notice tone="negative" title="KI-Entwurf nicht verfügbar">{error}</Notice>
        <Flex justifyContent="Start">
          <Button variant="tinted" onPress={generate}>Erneut versuchen</Button>
        </Flex>
      </Stack>
    );
  }

  return (
    <Stack className="cl-v2-stack">
      {!online && (
        <Notice tone="critical">
          Runner nicht erreichbar — »Neu generieren« und »In JIRA anlegen« sind gesperrt, bis er wieder antwortet.
        </Notice>
      )}
      {warnings.map((w) => (
        <Notice key={w} tone="critical">{w}</Notice>
      ))}
      {jiraKey && !jira && (
        <Notice tone="info">
          Achtung: Für diese Änderung gibt es schon <strong>{jiraKey}</strong>.
        </Notice>
      )}
      {jira && (
        <Notice tone="positive">
          In JIRA angelegt:{" "}
          <a href={jira.url} target="_blank" rel="noreferrer">{jira.key}</a>
        </Notice>
      )}
      {createError && <Notice tone="negative">{createError}</Notice>}

      <TextField label="Titel" value={title} onChange={setTitle} className="prn-fill" />
      <div>
        <label className="prn-field-label" htmlFor={`${ids}-body`}>Beschreibung (Markdown)</label>
        <textarea
          id={`${ids}-body`}
          className="prn-input cl-v2-mono"
          value={body}
          rows={12}
          onChange={(e) => setBody(e.target.value)}
        />
      </div>
      <div>
        <div className="cl-v2-label">Vorschau</div>
        <div className="markdown-preview cl-v2-md" dangerouslySetInnerHTML={{ __html: previewHtml }} />
      </div>

      {confirmRegenerate && (
        <Notice tone="critical" title="Entwurf neu generieren?">
          <Stack className="cl-v2-stack">
            <span>Deine Änderungen am Entwurf gehen verloren.</span>
            <Flex className="cl-v2-actions" justifyContent="End">
              <Button variant="plain" onPress={() => setConfirmRegenerate(false)}>Abbrechen</Button>
              <Button variant="filled" onPress={generate}>Verwerfen und neu generieren</Button>
            </Flex>
          </Stack>
        </Notice>
      )}

      <Flex className="cl-v2-actions" justifyContent="End">
        <Button variant="plain" isDisabled={!online} onPress={regenerate}>Neu generieren</Button>
        <Button
          variant="filled"
          isDisabled={!online || creating || jira != null || !title.trim() || !body.trim()}
          onPress={createJira}
        >
          <Icon name={princeIcon("connected")} size={16} />
          {creating ? "Lege an …" : "In JIRA anlegen"}
        </Button>
      </Flex>
    </Stack>
  );
}
